/**
 * Hero — brand-guide "Website Header Example" treatment.
 *
 * Mirrors the lockup from `/Assets/LOGOs/webnova-hero-sample.png`:
 *   · Two-column grid. Copy on the left, dark möbius mark on the right.
 *   · Eyebrow uses periods between phrases ("WE DESIGN. WE BUILD. WE
 *     ELEVATE.") matching the brand-guide treatment exactly.
 *   · Mark is the DARK master variant (matte black with brass-edge
 *     highlights), not the lighter champagne version. The brand guide
 *     wants the mark reading as substantial / present, not glowing.
 *   · Single CTA, gold-outlined uppercase pill, points at #contact —
 *     "See the work" sends a visitor away from the conversion path,
 *     which is the wrong move for a sales hero.
 *   · No spotlight wash, no aurora, no decorative animation past the
 *     entrance. Restraint is the whole point.
 *
 * Performance notes:
 *   · Hero mark ships as WebP first (.webp ~126 KB) with PNG fallback.
 *   · `fetchPriority="high"` and an `<picture>` source pair so the LCP
 *     candidate isn't gated on JS.
 *
 * Mobile behavior: below 900px the grid collapses and the visual is
 * hidden — a giant möbius squashed into a 360px column reads as
 * decoration interfering with copy.
 */
export default function Hero() {
  return (
    <section id="top" className="section hero hero-split">
      <div className="wrap hero-grid">
        <div className="hero-copy">
          {/* Eyebrow — caps gold trio with PERIODS between phrases,
              matching the brand-guide reference. The periods aren't
              full stops; they're the brand's separator glyph and sit
              tight to the words. */}
          <p className="eyebrow eyebrow-trio" aria-label="WebNova brand promise">
            <span>We design.</span> <span>We build.</span> <span>We elevate.</span>
          </p>

          <h1>
            <span className="line">
              <span>Your work is good.</span>
            </span>
            <span className="line">
              <span>
                Your website should <em className="serif">show&nbsp;it.</em>
              </span>
            </span>
          </h1>

          <p className="sub">
            Carefully built websites for businesses that deserve better — designed and shipped by
            the same person who picks up the phone a year from now. No templates, no outsourcing.
          </p>

          {/* Single CTA. Routes to the conversion section (#contact),
              not to the work showcase — sending visitors AWAY from the
              CTA is conversion-killing on a sales hero. The nav and
              the Showcase section both still link to #work for anyone
              who wants to see the case studies first. */}
          <div className="actions">
            <a className="btn btn-ghost btn-cta" href="#contact">
              Book a free critique
              <ArrowRight />
            </a>
          </div>

          {/* Quiet credibility strip — three factual hooks that anchor
              the hero before the visitor scrolls. No invented metrics,
              no fake logos. Replace with a real testimonial when one
              is available from Chef Zeitoun or Vienna Aquarium. */}
          <ul className="hero-creds" aria-label="At a glance">
            <li>Northern Virginia</li>
            <li>Free written critique</li>
            <li>4–6 week turnaround</li>
          </ul>
        </div>

        {/* Right column — DARK master mark. WebP first for ~94% size
            reduction over the PNG; PNG fallback for browsers that
            can't decode WebP (vanishingly rare in 2026 but cheap to
            keep). */}
        <div className="hero-visual" aria-hidden="true">
          <picture>
            <source
              type="image/webp"
              srcSet="/brand/webnova-mark-hero-dark.webp 1x, /brand/webnova-mark-hero-dark-2x.webp 2x"
            />
            <img
              className="hero-mark"
              src="/brand/webnova-mark-hero-dark.png"
              srcSet="/brand/webnova-mark-hero-dark.png 1x, /brand/webnova-mark-hero-dark-2x.png 2x"
              alt=""
              width={1492}
              height={1023}
              fetchPriority="high"
              decoding="async"
              draggable={false}
            />
          </picture>
        </div>
      </div>
    </section>
  );
}

function ArrowRight() {
  return (
    <span className="arrow" aria-hidden="true">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12h14M13 5l7 7-7 7" />
      </svg>
    </span>
  );
}
